/**
 * Utility composable for handling planning date conversions
 * Handles the conversion between user-facing "last actual" dates and backend "planning begin" dates
 *
 * The issue: fpna_begin_date_string in the backend represents the start of planning period,
 * but in the UI we show it as "last actual" date. This creates confusion where:
 * - User selects May as "last actual"
 * - Planning should start from June
 * - But we send May as fpna_begin_date_string (which should be June)
 *
 * This utility provides consistent conversion between:
 * - User UI: "Last Actual" date (what user sees and selects)
 * - Backend: fpna_begin_date_string (actual planning start date = last actual + 1 month)
 */

import moment from 'moment'

export interface DateObject {
  month: number // 0-based (0 = January, 11 = December)
  year: number
}

/**
 * Converts a user-selected "last actual" date to the planning begin date for backend
 * @param lastActualDate - The last actual date selected by user (0-based month)
 * @returns Planning begin date (last actual + 1 month) in YYYY-MM format
 */
export function convertLastActualToPlanningBegin(lastActualDate: DateObject): string {
  const lastActualMoment = moment().year(lastActualDate.year).month(lastActualDate.month).startOf('month')
  const planningBeginMoment = lastActualMoment.add(1, 'month')
  return planningBeginMoment.format('YYYY-MM')
}

/**
 * Converts a backend planning begin date to the "last actual" date for UI display
 * @param planningBeginDateString - Backend fpna_begin_date_string in YYYY-MM format
 * @returns Last actual date object (planning begin - 1 month) with 0-based month
 */
export function convertPlanningBeginToLastActual(planningBeginDateString: string): DateObject {
  const [year, month] = planningBeginDateString.split('-').map(Number)
  const planningBeginDate = new Date(year, month - 1, 1) // month-1 because Date constructor expects 0-based month
  const lastActualDate = new Date(planningBeginDate.getFullYear(), planningBeginDate.getMonth() - 1, 1)

  return {
    month: lastActualDate.getMonth(), // Already 0-based
    year: lastActualDate.getFullYear(),
  }
}

/**
 * Converts a date string to DateObject for UI date pickers
 * @param dateString - Date string in YYYY-MM format
 * @returns DateObject with 0-based month
 */
export function parseDateStringToDateObject(dateString: string): DateObject {
  const [year, month] = dateString.split('-').map(Number)
  return {
    month: month - 1, // Convert to 0-based month for date picker
    year,
  }
}

/**
 * Converts a DateObject to date string for backend
 * @param dateObj - DateObject with 0-based month
 * @returns Date string in YYYY-MM format
 */
export function formatDateObjectToString(dateObj: DateObject): string {
  const month = (dateObj.month + 1).toString().padStart(2, '0') // Convert to 1-based
  return `${dateObj.year}-${month}`
}

/**
 * Gets the actual last actual date from fpna_begin_date_string for display purposes
 * This is used throughout the application when we need to show the "last actual" date
 * @param fpnaBeginDateString - Backend fpna_begin_date_string
 * @returns The actual last actual date string in YYYY-MM format
 */
export function getLastActualFromPlanningBegin(fpnaBeginDateString: string): string {
  const lastActualDateObj = convertPlanningBeginToLastActual(fpnaBeginDateString)
  return formatDateObjectToString(lastActualDateObj)
}

/**
 * Hook for managing planning date state with automatic conversion
 */
export function usePlanningDateUtils() {
  return {
    convertLastActualToPlanningBegin,
    convertPlanningBeginToLastActual,
    parseDateStringToDateObject,
    formatDateObjectToString,
    getLastActualFromPlanningBegin,
  }
}

/**
 * Gets the display date for UI from fpna_begin_date_string
 * This should be used wherever fpna_begin_date_string is displayed as "last actual"
 * @param fpnaBeginDateString - Backend fpna_begin_date_string
 * @returns The last actual date string for display (planning begin - 1 month)
 */
export function getDisplayDateFromPlanningBegin(fpnaBeginDateString: string | undefined): string {
  if (!fpnaBeginDateString) return ''
  return getLastActualFromPlanningBegin(fpnaBeginDateString)
}

/**
 * Gets the actual planning begin date for calculations from fpna_begin_date_string
 * This should be used for date range calculations and logic that needs the real planning start
 * @param fpnaBeginDateString - Backend fpna_begin_date_string
 * @returns The actual planning begin date (same as input, but clarifies intent)
 */
export function getCalculationDateFromPlanningBegin(fpnaBeginDateString: string | undefined): string {
  if (!fpnaBeginDateString) return ''
  return fpnaBeginDateString
}
